// src/components/Handicap.js
import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Paper,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, app } from "../../firebase-config";
import { useAuth } from "../../context/AuthContext";

const Handicap = ({ currentHandicap }) => {
  const { user } = useAuth();
  const [requestedHandicap, setRequestedHandicap] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [imageFiles, setImageFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [statusDetailsDialogOpen, setStatusDetailsDialogOpen] = useState(false);
  const [latestRequest, setLatestRequest] = useState(null);

  // Fetch the latest handicap request for this user.
  useEffect(() => {
    if (user) {
      const fetchLatestRequest = async () => {
        try {
          const q = query(
            collection(db, "handicapRequests"),
            where("userId", "==", user.uid),
            orderBy("timestamp", "desc"),
            limit(1)
          );
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const latestDoc = querySnapshot.docs[0].data();
            setLatestRequest({ id: querySnapshot.docs[0].id, ...latestDoc });
          } else {
            setLatestRequest(null);
          }
        } catch (error) {
          console.error("Error fetching latest handicap request:", error);
        }
      };
      fetchLatestRequest();
    }
  }, [user]);

  // Helper: determine chip background color.
  const getChipBgColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "#de8c52";
      case "approved":
        return "#AFE1AF";
      case "rejected":
        return "#de2c2c";
      default:
        return "grey";
    }
  };

  // Handle file selection and generate preview URLs.
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      setImageFiles(filesArray);
      const previews = filesArray.map((file) => URL.createObjectURL(file));
      setPreviewUrls(previews);
    }
  };

  // Handle form submission.
  const handleSubmit = async (e) => {
    e && e.preventDefault();
    setStatusMsg("");

    if (!requestedHandicap) {
      setStatusMsg("Please enter a new handicap.");
      return;
    }

    const parsedRequested = parseFloat(requestedHandicap);

    // If the handicap is approved, require that the new value is different.
    if (user.isHandicapApproved && parsedRequested === user.handicap) {
      setStatusMsg("New handicap must differ from your current approved handicap.");
      return;
    }

    let imageUrls = [];
    try {
      setUploading(true);
      // Upload images if provided.
      if (imageFiles.length > 0) {
        const storage = getStorage(app);
        const uploadPromises = imageFiles.map((file) => {
          const filePath = `handicapRequests/${user.uid}/${Date.now()}_${file.name}`;
          const storageRef = ref(storage, filePath);
          return uploadBytes(storageRef, file).then(() => getDownloadURL(storageRef));
        });
        imageUrls = await Promise.all(uploadPromises);
      }

      // Create a new handicap request document in Firestore.
      await addDoc(collection(db, "handicapRequests"), {
        userId: user.uid,
        name: user.firstName + " " + user.lastName,
        currentHandicap: user.handicap,
        requestedHandicap: parsedRequested,
        imageUrls, // Array of image download URLs (or empty if none)
        timestamp: serverTimestamp(),
        status: "pending",
        notes: "",
      });

      setStatusMsg("Handicap update request submitted and pending approval.");
      setRequestedHandicap("");
      setImageFiles([]);
      setPreviewUrls([]);
      setUploading(false);
    } catch (error) {
      setUploading(false);
      console.error("Error submitting handicap request: ", error);
      setStatusMsg("Failed to submit request. Please try again.");
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 2, my: 2, position: "relative" }}>
      {/* Status Chip in top-right */}
      {latestRequest && latestRequest.status && (
        <Tooltip title="Click for status details" arrow>
          <Chip
            label={
              latestRequest.status.charAt(0).toUpperCase() +
              latestRequest.status.slice(1)
            }
            onClick={() => setStatusDetailsDialogOpen(true)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: getChipBgColor(latestRequest.status),
              color: "white",
              cursor: "pointer",
            }}
          />
        </Tooltip>
      )}

      <Typography variant="h6" gutterBottom>
        Request Handicap Change
      </Typography>
      <Typography variant="body2" gutterBottom>
        Your official handicap remains unchanged until an admin approves your update.
      </Typography>
      <div>
        <TextField
          label="New Handicap"
          type="number"
          value={requestedHandicap}
          onChange={(e) => setRequestedHandicap(e.target.value)}
          fullWidth
          margin="normal"
          InputProps={{
            inputProps: {
              style: { WebkitAppearance: "none", MozAppearance: "textfield" },
            },
          }}
          sx={{
            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
              display: "none",
            },
          }}
        />
        {/* Hidden file input */}
        <input
          accept="image/*"
          id="handicap-image-upload"
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <label htmlFor="handicap-image-upload">
              <Button variant="contained" component="span">
                {imageFiles.length > 0 ? "Change Images" : "Upload Images"}
              </Button>
            </label>
            <Tooltip
              title="Upload screenshots/images of your handicap index from apps like GHIN, 18 Birdies, or TheGrint."
              arrow
            >
              <IconButton
                size="small"
                onClick={() => setInfoDialogOpen(true)}
                sx={{ cursor: "pointer" }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={uploading}
              fullWidth
            >
              Submit Request
            </Button>
          </Box>
        </Box>
        {imageFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">
              {`Selected ${imageFiles.length} file${
                imageFiles.length > 1 ? "s" : ""
              }:`}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {previewUrls.map((url, idx) => (
                <img
                  key={idx}
                  src={url}
                  alt={`preview ${idx + 1}`}
                  style={{
                    height: 80,
                    width: 80,
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        {uploading && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Uploading images...
          </Typography>
        )}
        {statusMsg && (
          <Typography variant="body2" color="#FC6A03" sx={{ mt: 1 }}>
            {statusMsg}
          </Typography>
        )}
      </div>
      {/* Info Dialog Popup */}
      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)}>
        <DialogTitle>Handicap Information</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Upload screenshots or images of your handicap index from popular apps like GHIN, 18 Birdies, or TheGrint.
            These images help validate your requested handicap update.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Status Details Dialog */}
      <Dialog
        open={statusDetailsDialogOpen}
        onClose={() => setStatusDetailsDialogOpen(false)}
      >
        <DialogTitle>Status Details</DialogTitle>
        <DialogContent dividers>
          {latestRequest ? (
            <>
              <Typography variant="body1">
                <strong>Requested Handicap:</strong> {latestRequest.requestedHandicap}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Current Handicap:</strong> {latestRequest.currentHandicap}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Status:</strong>{" "}
                {latestRequest.status.charAt(0).toUpperCase() +
                  latestRequest.status.slice(1)}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Admin Notes:</strong>{" "}
                {latestRequest.notes
                  ? latestRequest.notes
                  : "No admin notes provided."}
              </Typography>
            </>
          ) : (
            <Typography variant="body1">No status details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDetailsDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Handicap;